import { useEffect, useRef, useState } from "react";
import banner from "../../images/banner.jpg";
import { Carousel } from "react-bootstrap";
import { AnimatedPage } from "../../core/AnimatedPage";

/**
 * AutoResizingIframe
 *
 * Behavior:
 * - If iframe is same-origin, attempts to read content height and resize.
 * - If cross-origin, falls back to a CSS-driven responsive container.
 * - Also listens for postMessage({ type: 'setHeight', height: <number> }) from iframe to set height.
 *
 * Props:
 * - src: string
 * - title: string
 * - height: number | string (optional) default 600 (used for cross-origin fallback)
 * - aspectRatio: string (optional) e.g. '56.25%' for 16:9 (uses padding-bottom trick)
 * - allowFullScreen, referrerPolicy, loading etc. pass through
 */
const AutoResizingIframe = ({
  src,
  title = "iframe",
  height = 600,
  aspectRatio, // e.g. '56.25%' -> height is calculated from width (padding-bottom)
  style = {},
  ...props
}) => {
  const iframeRef = useRef(null);
  const [dynamicHeight, setDynamicHeight] = useState(height);

  // try to resize for same-origin frames
  const trySameOriginResize = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentWindow?.document;
      if (!doc) return;
      const body = doc.body;
      const html = doc.documentElement;
      const newHeight = Math.max(
        body?.scrollHeight || 0,
        html?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.offsetHeight || 0
      );
      if (newHeight && newHeight > 0) {
        setDynamicHeight(newHeight);
      }
    } catch (err) {
      // Cross-origin — cannot access iframe DOM
      // console.debug("same-origin resize failed:", err);
    }
  };

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // If src changes, try resizing again (useful for same-origin)
    const onLoad = () => {
      trySameOriginResize();
    };
    iframe.addEventListener("load", onLoad);

    return () => {
      iframe.removeEventListener("load", onLoad);
    };
  }, [src]);

  // Listen for postMessage from iframe content to set height
  useEffect(() => {
    const onMessage = (e) => {
      // optional: validate origin if you know the iframe origin
      // if (e.origin !== 'https://your-trusted-origin.com') return;
      const data = e.data;
      if (data && typeof data === "object" && data.type === "setHeight") {
        const h = Number(data.height);
        if (!Number.isNaN(h) && h > 0) setDynamicHeight(h);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // container styles: if aspectRatio provided, use padding-bottom trick for responsive iframe
  if (aspectRatio) {
    // aspectRatio expected as percentage string like '56.25%' (for 16:9)
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          paddingBottom: aspectRatio,
          height: 0,
          overflow: "hidden",
          ...style,
        }}
      >
        <iframe
          ref={iframeRef}
          src={src}
          title={title}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: "0",
          }}
          {...props}
        />
      </div>
    );
  }

  // otherwise use width:100% and dynamicHeight px (from same-origin or fallback)
  return (
    <div style={{ width: "100%", ...style }}>
      <iframe
        ref={iframeRef}
        src={src}
        title={title}
        style={{
          width: "100%",
          height: dynamicHeight ? `${dynamicHeight}px` : `${height}px`,
          border: "0",
          overflow: "hidden",
        }}
        {...props}
      />
    </div>
  );
};

export default AutoResizingIframe;

export const Content = () => {
  return (
    <div>
      <Carousel variant="dark">
        <Carousel.Item>
          <div className="overlay">
            <h1 className="page-title">Contact Us</h1>
          </div>
          <img
            className="header-banner"
            src={banner}
            alt="banner"
          />
        </Carousel.Item>
      </Carousel>
      <AnimatedPage>
        <div className="contact-form">
          <div className="row">
            <div className="col-md-6">
              <AutoResizingIframe
                src="https://login.high1digital.com/widget/form/68f72f7e497fd"
                id="inline-68f72f7e497fd"
                data-form-name="Lead Form"
                data-layout-iframe-id="inline-68f72f7e497fd"
                data-form-id="68f72f7e497fd"
                data-height="900"
                height={900}
                title="Lead Form"
              ></AutoResizingIframe>{" "}
            </div>
            <div
              className="col-md-6"
              style={{ padding: "3%" }}
            >
              <AutoResizingIframe
                title="High1Digital"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3448.100062485377!2d74.94428141551879!3d30.20569141789141!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39173394355ac44f%3A0xb3c58a34a3fb2b1e!2sHigh1Digital!5e0!3m2!1sen!2sin!4v1670490561782!5m2!1sen!2sin"
                allowFullScreen={true}
                loading="lazy"
                aspectRatio="56.25%"
                referrerPolicy="no-referrer-when-downgrade"
              ></AutoResizingIframe>
            </div>
          </div>
        </div>
      </AnimatedPage>
    </div>
  );
};
